import { Component, OnInit, viewChild, computed, ChangeDetectionStrategy, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MessageDialog } from '../components/message-dialog/message-dialog.component';
import { combineLatest, delay, finalize, map, of, startWith, switchMap, tap } from 'rxjs';
import { MakingWavesLoaderService } from '../../shared/services';
import { getCurrentUser } from '@aws-amplify/auth';
import { ApplicationEnum, Campaign, EngagementTypeEnum } from 'src/app/models';
import { CreateCampaignLogicService } from '../services/create-campaign-logic.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CampaignCreatedDialog } from '../components/campaign-created-diaog/created-dialog.component';
import { NavigationService } from '../../shared/services';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LutItem } from 'src/app/models/lut-item.model';
import { CognitoService } from '../../shared/services/cognito.service';
import { LutApiService } from '../../groups/services/lut-api.service';
import { LutApplicationItem, LutTables } from '../model/lut.model';
import { IMessageDialogData } from '../components/message-dialog/message-dialog-data.interfcae';
import { TG_ANCHORS_MESSAGE } from '../components/message-dialog/message-dialog-data.const';
import { PostMessageFormComponent } from '../components/post-message-form/post-message-form.component';
import { LikesForPostComponent } from '../components/likes-for-post/likes-for-post.component';
import { CommentsOnPostComponent } from '../components/comments-on-post/comments-on-post.component';
import { FollowUserComponent } from '../components/follow-user/follow-user.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-create-campaign',
    templateUrl: './create-campaign.component.html',
    styleUrls: ['./create-campaign.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule, MatButtonModule, PostMessageFormComponent, LikesForPostComponent, CommentsOnPostComponent, FollowUserComponent]
})
export class CreateCampaignComponent implements OnInit {

  // Injected services
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private cognitoService = inject(CognitoService);
  private createCampaignService = inject(CreateCampaignLogicService);
  private lutApiService = inject(LutApiService);
  private loaderService = inject(MakingWavesLoaderService);
  private navigationService = inject(NavigationService);
  private snackBar = inject(MatSnackBar);
  public dialog = inject(MatDialog);

  lutTables = signal<LutTables>({
      lut_applications: [],
      lut_avatar_status: [],
      lut_countries: [],
      lut_owner_units: [],
      lut_regions: []
  });

  private allEngagements = [
    { id: EngagementTypeEnum.post, name: 'פוסט', appIds: ['TG','FB', 'TGE', 'TK'] },
    { id: EngagementTypeEnum.likesOnPost, name: 'לייק לפוסט', appIds: ['FB', 'TK'] },
    { id: EngagementTypeEnum.commentsOnPost, name: 'תגובות לפוסט', appIds: ['FB', 'TK'] },
    { id: EngagementTypeEnum.followUser, name: 'עקוב', appIds: ['TK'] },
  ];

  engagementTypeList = signal<LutItem[]>([]);

  ownerUnits = signal<LutItem[]>([]);

  childComponent = viewChild<PostMessageFormComponent | LikesForPostComponent | CommentsOnPostComponent | FollowUserComponent>('child');

  public campaignForm = this.fb.nonNullable.group({
    campaignName: ['', [Validators.required]],
    ownerUnitId: ['', [Validators.required]],
    regionId: ['', [Validators.required]],
    application: [{ id: '', name: '' } as LutItem, [Validators.required]],
    engagementType: [{value:{ id: '', name: '' } as LutItem, disabled:true}, [Validators.required]],
    gender: [''],
    ageFrom: [null as number | null],
    ageTo: [null as number | null]
  });


  public isMandatoryFieldsFilled = toSignal(
    combineLatest([
      this.campaignForm.controls.campaignName.valueChanges.pipe(startWith(this.campaignForm.controls.campaignName.value)),
      this.campaignForm.controls.ownerUnitId.valueChanges.pipe(startWith(this.campaignForm.controls.ownerUnitId.value)),
      this.campaignForm.controls.regionId.valueChanges.pipe(startWith(this.campaignForm.controls.regionId.value)),
      this.campaignForm.controls.application.valueChanges.pipe(startWith(this.campaignForm.controls.application.value)),
      this.campaignForm.controls.engagementType.valueChanges.pipe(startWith(this.campaignForm.controls.engagementType.value))
    ]).pipe(
      map(([campaignName, ownerUnit, regionId, app, engagementType]) =>
        !!campaignName && !!ownerUnit && !!regionId && !!app && !!engagementType
      )
    ),
    { initialValue: false }
  );



  ngOnInit(): void {    
    of(null).pipe(
      // Delay the start just enough to avoid the "ExpressionChanged" error
      delay(0),      
      tap(() => this.loaderService.showLoader()),
      // Switch to the actual data call
      switchMap(() => this.lutApiService.getAllLutTables$()),
      // When this chain finishes (success or error), hide the loader
      finalize(() => this.loaderService.hideLoader()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res) => {
        this.lutTables.set(res);
        this.setOwnerUnits();
      },
      error: (err) => console.error('Error loading LUT tables:', err)
    });

  }

  setOwnerUnits(){
      this.cognitoService.getUserGroups().then(groups => {
        const lut = this.lutTables();
        if (groups.includes("ADMIN")) {
          this.ownerUnits.set(lut.lut_owner_units);
        } else {
          this.ownerUnits.set(lut.lut_owner_units.filter(u => groups.includes(u.id.toString())));
        }

        if (this.ownerUnits().length === 1) {
          this.campaignForm.get('ownerUnitId')?.setValue(this.ownerUnits()[0].id.toString());
          this.campaignForm.get('ownerUnitId')?.disable();
        } else {
          this.campaignForm.get('ownerUnitId')?.enable();
        }
    });
  }


onApplicationChange(app: LutApplicationItem) {
    // filter engagements for the selected appId
    const filtered = this.allEngagements.filter(engagement =>
      engagement.appIds.includes(app.id)
    );
    this.engagementTypeList.set(filtered);

    if (filtered.length > 0) {
      // there is at least one engagement for this appId. Set the first one as default
      this.campaignForm.controls.engagementType.setValue(filtered[0]);
    } else {
      this.campaignForm.controls.engagementType.setValue({"id":'',"name":''});
    }

    // enable engagement control after selecting an app
    const engagementControl = this.campaignForm.get('engagementType');
    engagementControl?.enable({ emitEvent: false });

    if (app.id === ApplicationEnum.telegram || app.id === ApplicationEnum.telegramBt) {
      this.campaignForm.patchValue({ gender: '', ageFrom: null, ageTo: null });
    }
  }

  public get childForm(): FormGroup | undefined {
    return this.childComponent()?.engagementFormGroup;
  }

  // Bridge form statusChanges into signals
  private campaignFormValid = toSignal(
    this.campaignForm.statusChanges.pipe(
      startWith(this.campaignForm.status),
      map(status => status === 'VALID')
    ),
    { initialValue: false }
  );

  private childFormValid = toSignal(
    toObservable(this.childComponent).pipe(
      switchMap(child => {
        if (!child?.engagementFormGroup) return of(false);
        return child.engagementFormGroup.statusChanges.pipe(
          startWith(child.engagementFormGroup.status),
          map(status => status === 'VALID')
        );
      })
    ),
    { initialValue: false }
  );

  isBothFormsValid = computed(() => this.campaignFormValid() && this.childFormValid());

  get selectedApplicationId(): string | undefined {
    const selectedApplicationId = this.campaignForm.getRawValue().application.id.toString()
    return selectedApplicationId;
  }

  get selectedApplication(): LutApplicationItem | null {
    const selectedApplicationId = this.campaignForm.getRawValue().application.id
    const lutApplicationItem = this.lutTables().lut_applications.find(a => a.id === selectedApplicationId);
    return lutApplicationItem ?? null;
  }

  get selectedOwnerUnitId(): string | undefined {
    const selected = this.campaignForm.getRawValue().ownerUnitId.toString()
    return selected;
  }

  get selectedOwnerRegionId(): string | undefined {
    const selected = this.campaignForm.getRawValue().regionId.toString()
    return selected;
  }

  get selectedGender(): string | undefined {
    return this.campaignForm.getRawValue().gender || undefined;
  }

  get selectedAgeFrom(): number | undefined {
    return this.campaignForm.getRawValue().ageFrom || undefined;
  }

  get selectedAgeTo(): number | undefined {
    return this.campaignForm.getRawValue().ageTo || undefined;
  }

  get isPostMessageSelected(): boolean {
    const engagementValue = this.campaignForm.getRawValue().engagementType;
    return Number(engagementValue.id) === EngagementTypeEnum.post;
  }

  get isLikesForPostSelected(): boolean {
    const engagementValue = this.campaignForm.getRawValue().engagementType;
    return Number(engagementValue.id) === EngagementTypeEnum.likesOnPost;    
  }

  get isCommentsOnPostSelected(): boolean {
    const engagementValue = this.campaignForm.getRawValue().engagementType;
    return Number(engagementValue.id) === EngagementTypeEnum.commentsOnPost;
  }

  get isFollowUserSelected(): boolean {
    const engagementValue = this.campaignForm.getRawValue().engagementType;
    return Number(engagementValue.id) === EngagementTypeEnum.followUser;
  }

  get isTgselected() {
    const applicationId = this.campaignForm.getRawValue().application.id;
    return applicationId === ApplicationEnum.telegram || applicationId === ApplicationEnum.telegramBt;
  }

  get isTgeSelected() {
    const applicationId = this.campaignForm.getRawValue().application.id;
    return applicationId === ApplicationEnum.telegramBt;
  }

  get isFbselected() {
    const applicationId = this.campaignForm.getRawValue().application.id;
    return applicationId === ApplicationEnum.facebook;
  }

  async onSendClick(): Promise<void> {
    const formValues =  this.campaignForm.getRawValue();
    const application = formValues.application;

    if (application.id === ApplicationEnum.facebook || application.id === ApplicationEnum.telegramBt || application.id === ApplicationEnum.tiktok) {
      const child = this.childComponent();
      if (child && 'validateBeforeSend' in child) {
        const errors = await (child as PostMessageFormComponent).validateBeforeSend();
        if (errors.length > 0) {
          this.openMessageDialog({
            header: 'בדיקת חוקיות',
            text: '',
            lines: errors,
            showOkBtn: false,
            showCancelBtn: true,
            showCloseBtn: false
          });
          return;
        }
      }
      this.openMessageDialog({
        header: 'בדיקת חוקיות',
        text: 'בדיקת חוקיות הסתיימה בהצלחה',
        showOkBtn: true,
        showCancelBtn: true,
        showCloseBtn: false
      });
      return;
    }

    if (application.id === ApplicationEnum.telegram) {
      const anchors = this.getChildFormValueByKey('anchors') || [];
      const anchorsCount = anchors.length;
      if (anchorsCount <= 15000) {
        this.send();
      } else {
        this.openMessageDialog(TG_ANCHORS_MESSAGE(anchorsCount));
      }
    }

    if (application.id === ApplicationEnum.tiktok) {
      this.send();
    }

  }


  private openMessageDialog(data:IMessageDialogData) {
    const dialogRef = this.dialog.open(MessageDialog, {
      width: '512px',
      data: data,
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed) {
        this.send();
      }
    });
  }

  private async send() {
    this.loaderService.showLoader();    
    const userName = await this.getUsername()
    const campaignFormValues = this.campaignForm.getRawValue();
    const applicationId = (campaignFormValues.application as LutApplicationItem).id    

    const campaign: Campaign = {
      campaignName: campaignFormValues.campaignName,
      ownerUnitId: campaignFormValues.ownerUnitId,
      applicationId: applicationId,
      regionId: campaignFormValues.regionId,
      createdBy: userName,
      gender: campaignFormValues.gender || undefined,
      ageFrom: campaignFormValues.ageFrom || undefined,
      ageTo: campaignFormValues.ageTo || undefined
    };

    switch (applicationId) {
      case ApplicationEnum.facebook:
        campaign.engagements = this.getFbEngagements(campaignFormValues.engagementType)
        break
      case ApplicationEnum.telegramBt:
        campaign.engagements = this.getTgeEngagements(campaignFormValues.engagementType)
        break
      case ApplicationEnum.telegram:
        campaign.engagements = this.getTgEngagements(campaignFormValues.engagementType)
        break
      case ApplicationEnum.tiktok:
        campaign.engagements = this.getTkEngagements(campaignFormValues.engagementType)
        break
    }

    this.createNewCampaign(campaign)
  }

  getTgEngagements(engagementType: LutItem): any[] {
    const image = this.getChildFormValueByKey('imageFile')?.name;
    const video = this.getChildFormValueByKey('videoFile')?.name;    
    const media = [image, video].filter(Boolean);

    const engagements = [{
      engagementType: {"id":engagementType.id, "name": engagementType.name},
      text: this.getChildFormValueByKey('text') ?? '',
      media: media,
      anchorType: this.getChildFormValueByKey('anchorType'),
      anchors: this.getChildFormValueByKey('anchors'),     
    }];

    return engagements;
  }

  getTgeEngagements(engagementType: LutItem): any[] {
    const image = this.getChildFormValueByKey('imageFile')?.name;
    const video = this.getChildFormValueByKey('videoFile')?.name;
    const media = [image, video].filter(Boolean);
    const anchorType = this.getChildFormValueByKey('anchorType');
    const anchors = this.getChildFormValueByKey('anchors') ?? [];

    const engagement: Record<string, any> = {
      engagementType: {"id":engagementType.id, "name": engagementType.name},
      message: this.getChildFormValueByKey('text') ?? '',
      media: media,
    };

    if (anchorType === 'group') {
      engagement['group_urls'] = this.getChildFormValueByKey('groupUrls') ?? [];
    } else {
      engagement['users'] = anchors;
    }

    return [engagement];
  }

  getTkEngagements(engagementType: LutItem): any[] {
    let engagements: Record<string, any>[] = [];

    switch (engagementType.id) {

      case EngagementTypeEnum.post:
        const image = this.getChildFormValueByKey('imageFile')?.name;
        const video = this.getChildFormValueByKey('videoFile')?.name;
        const media = [image, video].filter(Boolean);
        engagements = [{
          engagementType: {"id":engagementType.id, "name": engagementType.name},
          message: this.getChildFormValueByKey('text') ?? '',
          media: media,
        }];
        break;

      case EngagementTypeEnum.likesOnPost:
        engagements = [{
          engagementType: {"id":engagementType.id, "name": engagementType.name},
          postUrl: this.getChildFormValueByKey('postUrl') ?? '',
          numberOfLikes: this.getChildFormValueByKey('numberOfLikes') ?? 0,
          secondsBetweenLikes: this.getChildFormValueByKey('secondsBetweenLikes') ?? 0,
          startDateTime: this.getCombinedDatetime()
        }];
        break;

      case EngagementTypeEnum.commentsOnPost:
        engagements = [{
          engagementType: {"id":engagementType.id, "name": engagementType.name},
          postUrl: this.getChildFormValueByKey('postUrl') ?? '',
          intervalSeconds: this.getChildFormValueByKey('intervalSeconds') ?? 0,
          startDateTime: this.getCombinedDatetime(),
          commentList: this.getChildFormValueByKey('commentList') ?? []
        }];
        break;

      case EngagementTypeEnum.followUser:
        engagements = [{
          engagementType: {"id":engagementType.id, "name": engagementType.name},
          userUrl: this.getChildFormValueByKey('userUrl') ?? '',
          numberOfFollows: this.getChildFormValueByKey('numberOfFollows') ?? 0,
          secondsBetweenFollows: this.getChildFormValueByKey('secondsBetweenFollows') ?? 0,
          startDateTime: this.getCombinedDatetime()
        }];
        break;
    }
    return engagements;
  }

  getFbEngagements(engagementType: LutItem): any[] {
    let engagements: Record<string, any>[] = [];

    switch (engagementType.id) {

      case EngagementTypeEnum.post:
        const image = this.getChildFormValueByKey('imageFile')?.name;
        const video = this.getChildFormValueByKey('videoFile')?.name;
        const media = [image, video].filter(Boolean);

        engagements = [{
          engagementType: {"id":engagementType.id, "name": engagementType.name},
          message: this.getChildFormValueByKey('text') ?? '',
          media: media,
          group_urls: this.getChildFormValueByKey('groupUrls') ?? [],
        }];
        break;

      case EngagementTypeEnum.likesOnPost:        
        engagements = [{
          engagementType: {"id":engagementType.id, "name": engagementType.name},
          postUrl: this.getChildFormValueByKey('postUrl') ?? '',
          numberOfLikes: this.getChildFormValueByKey('numberOfLikes') ?? 0,
          secondsBetweenLikes: this.getChildFormValueByKey('secondsBetweenLikes') ?? 0,
          startDateTime: this.getCombinedDatetime()
        }]
        break;

      case EngagementTypeEnum.commentsOnPost:
        engagements = [{
          engagementType: {"id":engagementType.id, "name": engagementType.name},
          postUrl: this.getChildFormValueByKey('postUrl') ?? '',          
          intervalSeconds: this.getChildFormValueByKey('intervalSeconds') ?? 0,
          startDateTime: this.getCombinedDatetime(),
          commentList: this.getChildFormValueByKey('commentList') ?? []
        }]
        break;

      default:        
        break;          
    }
    return engagements;
  }

  getCombinedDatetime(): Date {
    const dateValue: Date = this.getChildFormValueByKey('startDate'); 
    const timeValue: string = this.getChildFormValueByKey('startTime');

    const [hours, minutes] = timeValue.split(':').map(Number);
    
    const combined = new Date(dateValue);
    combined.setHours(hours);
    combined.setMinutes(minutes);
    combined.setSeconds(0);
    
    return combined;
  }


  private createNewCampaign(campaign: Campaign) {

    this.loaderService.updateMessage('מעדכן נתוני קמפיין...')

    this.createCampaignService.postNewCampaign(campaign).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
        next: campaignId => {
          campaign.id = campaignId
          this.uploadMediaToS3(campaign)
        },
        error: err => {
          this.loaderService.hideLoader();
          console.error('Observable emitted an error:' + err.error);
          this.snackBar.open('שגיאה', 'סגור', { duration: 5000 });
        },
      });
  }

  private uploadMediaToS3(campaign: Campaign) {
    const image = this.getChildFormValueByKey('imageFile');
    const video = this.getChildFormValueByKey('videoFile');
    const file = image || video;
    if (file) {
      this.loaderService.updateMessage('מעלה קובץ לענן...');

      this.createCampaignService.uploadFileToS3(file, campaign.id!)
        .then((result: any) => {
          this.loaderService.hideLoader();
          this.showDialog(campaign)
        })
        .catch((err: any) => {
          this.loaderService.hideLoader();
          console.error('Observable emitted an error: ' + err)
          this.snackBar.open('שגיאה', 'סגור', { duration: 5000 });
        });
    } else {
      this.loaderService.hideLoader();
      this.showDialog(campaign)
    }
  }

  private async showDialog(campaign: Campaign) {
    const dialogRef = this.dialog.open(CampaignCreatedDialog, {
      width: '512px',
      data: { campaign },
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result)
        this.navigationService.goToCampaignStatusByQueryId(campaign.id!);
      else
        location.reload();
    });
  }


  private getChildFormValueByKey(key: string): any {    
    return this.childForm?.get(key)?.value;
  }


  private async getUsername() {
    try {
      const user = await getCurrentUser();
      return user.username;
    } catch (error) {
      console.error("Error fetching user:", error);
      throw error;
    }
  }



}
